import React, { useEffect, useRef } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import type { Brand } from '@flipdish/orgmanagement';
import { AtSign, Building07, Flag01, RefreshCcw02 } from '@flipdish/ui-icons';
import { AvatarLabelGroup } from '@flipdish/ui-library/components/atoms/Avatar';
import { Badge } from '@flipdish/ui-library/components/atoms/Badge';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import { Divider } from '@flipdish/ui-library/components/atoms/Divider';
import { type CountryCode, Flag } from '@flipdish/ui-library/components/atoms/Flag';
import { LoadingIndicator } from '@flipdish/ui-library/components/atoms/LoadingIndicator';
import { SearchInput } from '@flipdish/ui-library/components/atoms/SearchInput';
import {
  type DetailListItemType,
  DetailList,
} from '@flipdish/ui-library/components/molecules/DetailList';
import { SidePanel } from '@flipdish/ui-library/components/organisms/SidePanel';
import { getActiveLanguage, getTranslate } from 'react-localize-redux';

import { useBrandSelector } from '../../../../components/RMS/BrandSelector/hooks/useBrandSelector';
import { BrandCards } from './BrandCards';
import { type AccessRule, usePermissions } from './hooks/usePermissions';

type Props = {
  /** Called once the user has chosen another brand, so the shell can close the panel where it covers the page. */
  onBrandSwitched: () => void;
  /** "View more" was pressed on a brand's settings link; the link navigates, the shell closes a covering panel. */
  onViewBrand: () => void;
  /** Swaps the panel to the organisation switcher. */
  onSwitchOrganisation: () => void;
};

/** The country's display name in the user's language, or the code when the runtime cannot name it. */
const getCountryName = (countryCode: string | undefined, language: string): string => {
  if (!countryCode) return '';
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(countryCode) ?? countryCode;
  } catch {
    return countryCode;
  }
};

/** `Flag` only knows ISO 3166-1 alpha-2 codes; anything else keeps the generic icon. */
const asCountryCode = (countryCode: string | undefined): CountryCode | undefined =>
  countryCode && /^[A-Z]{2}$/.test(countryCode) ? (countryCode as CountryCode) : undefined;

// The sidebar's rule for its "Brand" settings item (`menuItemsConfig`), so the panel never links where
// the nav hides. `ViewBrand` is an AuthZ permission not yet in the typed union, hence the cast.
const brandSettingsAccess: AccessRule = {
  legacy: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
  authZ: ['ViewBrand', 'FlipdishStaff'] as AppState['permissions'],
};

/** The organisation summary and its brands as `SidePanel` content, opened from the header avatar. */
export const OrganisationPanel = ({
  onBrandSwitched,
  onViewBrand,
  onSwitchOrganisation,
}: Props) => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const { hasAccess, isFlipdishStaff } = usePermissions();
  const canViewBrandSettings = hasAccess(brandSettingsAccess);
  const activeLanguage = useAppSelector((state) => getActiveLanguage(state.locale)) as unknown;
  const language =
    typeof activeLanguage === 'string'
      ? activeLanguage
      : ((activeLanguage as { code?: string } | undefined)?.code ?? 'en');

  const {
    currentApp,
    currentOrg,
    orgId,
    brands,
    filteredBrands,
    loading,
    maxVisibleItems,
    handleSearch,
    handleChange,
  } = useBrandSelector({
    enabled: true,
    onClose: onBrandSwitched,
    onSwitchOrg: onSwitchOrganisation,
  });

  // Swapping back from the switcher unmounts the focused Back button; take focus so Tab stays in the panel.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const organisationName = currentOrg?.name || currentApp.Name || '';
  const organisationId = currentOrg?.orgId || orgId || '';
  const countryCode = asCountryCode(currentOrg?.countryCode);
  const showsSearch = brands.length > maxVisibleItems;

  const selectBrand = (brand: Brand, openInNewTab: boolean) =>
    handleChange(undefined, brand, openInNewTab);

  const orgType = currentOrg?.orgType;
  const organisationTypeBadge =
    orgType === 'demo' ? (
      <Badge size="sm" color="gray">
        {translate('Demo') as string}
      </Badge>
    ) : orgType === 'test' ? (
      <Badge size="sm" color="gray">
        {translate('Test') as string}
      </Badge>
    ) : (
      <Badge size="sm" color="success">
        {translate('Live_customer') as string}
      </Badge>
    );
  const typeRow: DetailListItemType = {
    id: 'type',
    icon: Building07,
    label: translate('Organisation_type') as string,
    value: organisationTypeBadge,
  };
  const details: DetailListItemType[] = [
    ...(isFlipdishStaff ? [typeRow] : []),
    {
      id: 'email',
      icon: AtSign,
      label: translate('Organisation_contact_email') as string,
      value: currentOrg?.emailAddress || '-',
    },
    {
      id: 'country',
      // The value names the country, so the flag is decorative.
      icon: countryCode ? <Flag code={countryCode} size="xs" label="" locale={language} /> : Flag01,
      label: translate('Country') as string,
      value: getCountryName(currentOrg?.countryCode, language) || '-',
    },
  ];

  const renderBrands = () => {
    // A brand switch re-reads the org for the new route; the cards stay up meanwhile so the chosen one keeps focus.
    if (loading && brands.length === 0) {
      return (
        <div className="flex justify-center py-6">
          <LoadingIndicator size="md" />
        </div>
      );
    }
    if (filteredBrands.length === 0) {
      return (
        <p className="text-sm text-tertiary" data-fd="organisation-panel-no-brands">
          {translate('No_brands_found', { orgId: organisationId }) as string}
        </p>
      );
    }
    return (
      <BrandCards
        brands={filteredBrands}
        brandId={currentApp?.AppId}
        label={translate('Brands') as string}
        viewMoreLabel={translate('View_more') as string}
        showViewMore={canViewBrandSettings}
        onSelectBrand={selectBrand}
        onSelectCurrentBrand={onBrandSwitched}
        onViewBrand={onViewBrand}
      />
    );
  };

  return (
    <>
      <SidePanel.Header>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="truncate pr-14 text-lg font-semibold text-primary outline-none"
        >
          {translate('Organisation_summary') as string}
        </h2>
      </SidePanel.Header>
      <SidePanel.Content data-fd="organisation-panel">
        <div className="flex flex-col gap-6">
          <AvatarLabelGroup
            size="xl"
            rounded={false}
            border
            src={currentOrg?.logoImageUrl || currentApp.LogoImageUrl || undefined}
            alt=""
            placeholderIcon={Building07}
            title={organisationName}
            subtitle={organisationId}
            data-fd="organisation-panel-identity"
          />
          <Divider />
        </div>

        <section className="flex flex-col gap-5" data-fd="organisation-panel-details">
          <h3 className="text-md font-semibold text-primary">
            {translate('Organisation_details') as string}
          </h3>
          <DetailList items={details} />
        </section>

        <Divider />

        <section className="flex flex-col gap-5" data-fd="organisation-panel-brands">
          <h3 className="text-md font-semibold text-primary">{translate('Brands') as string}</h3>
          {showsSearch && (
            <SearchInput
              aria-label={translate('Search_brands') as string}
              placeholder={translate('Search_brands') as string}
              onChange={handleSearch}
            />
          )}
          {renderBrands()}
        </section>
      </SidePanel.Content>
      <SidePanel.Footer variant="actions">
        <Button
          color="primary"
          size="md"
          iconLeading={RefreshCcw02}
          onPress={onSwitchOrganisation}
          data-fd="brand-selector-switch-org"
        >
          {translate('Switch_organisation') as string}
        </Button>
      </SidePanel.Footer>
    </>
  );
};

export default OrganisationPanel;
